/**
 * Foam type ↔ filename token lookup used by the custom-seat CAD form.
 *
 * When a reference/CAD filename is entered, we scan it for these tokens and
 * warn if the selected foam type does not match what the filename suggests.
 *
 * Structure of FOAM_FILENAME_CODES:
 *   key   = foam_type value stored on the form (specs.foam_type)
 *   value = one or more substrings that may appear in the filename
 *
 * Matching is case-insensitive substring search (e.g. "Seat_F32_v2.dxf"
 * detects foam type `f32`). A foam type can list several aliases so older
 * naming conventions still match (e.g. S4044 also accepts B40 / RG40).
 *
 * Add more strings to an array when a new filename abbreviation shows up
 * in the wild; keep the form key stable.
 */
export const FOAM_FILENAME_CODES = {
  // Soft F32 foam — also appears as OSS32 in some filenames
  f32: ['F32', 'OSS32'],
  // Medium RG50 foam
  rg50: ['RG50'],
  // Firm RG70 foam
  rg70: ['RG70'],
  // S4044 foam — legacy aliases B40 and RG40
  s4044: ['S4044', 'B40', 'RG40'],
  // Low-density LD24 foam
  ld24: ['LD24'],
  // Mixed / multi-foam builds
  mix: ['MIX']
}

/**
 * Returns foam type keys whose codes appear in the filename (case-insensitive).
 * May return multiple keys if the filename contains codes for more than one foam.
 */
export function detectFoamTypesFromFilename (filename) {
  if (!filename || typeof filename !== 'string') return []

  const upper = filename.toUpperCase()
  const detected = []

  Object.keys(FOAM_FILENAME_CODES).forEach(foamType => {
    const codes = FOAM_FILENAME_CODES[foamType]
    const matches = codes.some(code =>
      upper.includes(String(code).toUpperCase())
    )
    if (matches) detected.push(foamType)
  })

  return detected
}

/**
 * True when the filename contains at least one foam code and the selected
 * foam type is not among the detected types (empty selection is skipped).
 */
export function isFoamFilenameMismatch (filename, foamType) {
  if (!foamType) return false

  const detected = detectFoamTypesFromFilename(filename)
  if (detected.length === 0) return false

  return !detected.includes(foamType)
}
